/**
 * 69.29 (DECISIONS 239, 245; ARCHITECTURE §5.25): a tank's turret, laid by its gunner at the turret's own rates. The gun's
 * bearing is the gunner's heading against the world (stabilised: the hull turning under it does not carry it), turned at most
 * TANK.traverse a second toward what its gunner asks; its pitch at most TANK.elevateRate a second, within TANK.elevation. The
 * turret's yaw is that bearing off the hull's front — what its hit shape's turret part and the drawn turret stand turned by.
 * 69.46: a crewman lays it at CREWMAN.lay × the rates (`rate`, core/crewman.ts layRate). Pure.
 */
import type { GroundDials } from './fineTerrain';
import type { PlanetState, StructureState, Vec3 } from '../types';
import { TANK, TANK_MUZZLES } from '../constants/action';
import { vehicleFrame } from './vehicleFrame';
import type { PersonSet } from './scaleProfile';
import { turnHeading } from './surfaceWalk';
import { datan2, dcos, dsin } from './dmath';
import { vecAdd, vecDot, vecScale } from '../math';

/** Whether seat `index` of `s` is a tank's turret (its gunner's: the main gun and its coax). */
export function isTurretSeat(s: Pick<StructureState, 'type'>, index: number): boolean {
  return s.type === 'tank' && index === TANK.turretSeat;
}

/**
 * The turret laid a tick on: from the gunner's heading `from` and pitch `pitchFrom`, toward the intent's `turn` and `pitch`
 * at the turret's rates (× `rate`) — the gun's new bearing and pitch, and the turret's yaw off the hull's front for it.
 */
export function layTurret(s: Pick<StructureState, 'type' | 'surfaceDir' | 'drive'>, planet: PlanetState, ps: Pick<PersonSet, 'k' | 'fineAmplitude' | 'fineWavelength'>, person: number,
  from: Vec3, n: Vec3, turn: number, pitchFrom: number, pitch: number, dt: number, rate = 1): { heading: Vec3; aimPitch: number; turretYaw: number } {
  const step = TANK.traverse * rate * dt, rise = TANK.elevateRate * rate * dt;
  const heading = turnHeading(from, n, Math.max(-step, Math.min(step, turn)));
  const [lo, hi] = TANK.elevation;
  const was = Math.max(lo, Math.min(hi, pitchFrom)), want = Math.max(lo, Math.min(hi, pitch));
  const aimPitch = was + Math.max(-rise, Math.min(rise, want - was));
  const f = vehicleFrame(planet, s, person, ps);
  return { heading, aimPitch, turretYaw: datan2(vecDot(heading, f.left), vecDot(heading, f.front)) };
}

/**
 * 69.55 (DECISIONS 264 (2)): where the turret seat's round leaves — the main gun's muzzle or the coax's (TANK_MUZZLES), the
 * cradle pitched by `pitch` (within TANK.elevation, as drawn) and the turret turned by its yaw about its axis, on the
 * vehicle's frame (the one its hit shape and its drawn hull share). The drawn barrel's tip, not the gunner's eye above it.
 */
export function tankMuzzle(s: Pick<StructureState, 'type' | 'surfaceDir' | 'drive' | 'turretYaw'>, planet: PlanetState, ps: GroundDials, person: number, pitch: number, gun: 'main' | 'coax'): Vec3 {
  const f = vehicleFrame(planet, s, person, ps);
  const [lo, hi] = TANK.elevation, p = Math.max(lo, Math.min(hi, pitch)), cp = dcos(p), sp = dsin(p);
  const [mx, my, mz] = gun === 'main' ? [0, 0, TANK_MUZZLES.main] : TANK_MUZZLES.coax;
  // the cradle's pitch (its front up by p), from its pivot; then the turret's yaw about its axis (its front toward the hull's left)
  const x = TANK_MUZZLES.pivot[0] + mx, y = TANK_MUZZLES.pivot[1] + my * cp + mz * sp, z = TANK_MUZZLES.pivot[2] - my * sp + mz * cp;
  const yaw = s.turretYaw ?? 0, cy = dcos(yaw), sy = dsin(yaw);
  const tx = x * cy + z * sy, tz = TANK.turretPivotZ - x * sy + z * cy;
  return vecAdd(f.foot, vecAdd(vecScale(f.left, tx * person), vecAdd(vecScale(f.up, y * person), vecScale(f.front, tz * person))));
}
